import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../services/apiService'

/**
 * The draw for one event: every match with its round, date, time and court,
 * plus the control to build or rebuild it.
 */
const EventFixtures = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [draws, setDraws] = useState({})
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const [editing, setEditing] = useState(null)   // fixture id being moved
  const [form, setForm] = useState({})
  const [teams, setTeams] = useState([])
  const [adding, setAdding] = useState(false)
  const [openTeam, setOpenTeam] = useState(null)   // team whose squad is shown

  useEffect(() => { load() }, [eventId])

  const load = async () => {
    setLoading(true)
    try {
      const [res, teamRes] = await Promise.all([
        api.get(`/client-admin/events/${eventId}/fixtures`),
        api.get(`/client-admin/events/${eventId}/fixtures/teams`).catch(() => null),
      ])
      setDraws(res.data?.data || {})
      setTotal(res.data?.total || 0)
      setTeams(teamRes?.data?.data || [])
    } catch (err) {
      console.error('Error loading fixtures:', err)
      setMessage({ tone: 'error', text: 'Could not load the draw.' })
    } finally {
      setLoading(false)
    }
  }

  const generate = async (replace) => {
    setBusy(true)
    setMessage(null)
    try {
      const res = await api.post(`/client-admin/events/${eventId}/fixtures/generate`, { replace })
      setMessage({ tone: 'ok', text: res.data.message })
      await load()
    } catch (err) {
      const data = err.response?.data
      // 409 means a draw already exists; offer to rebuild rather than fail.
      if (err.response?.status === 409) {
        setMessage({ tone: 'warn', text: data.message, offerReplace: true })
      } else {
        setMessage({ tone: 'error', text: data?.message || 'Could not build the draw.' })
      }
    } finally {
      setBusy(false)
    }
  }

  const startEdit = (f) => {
    setEditing(f.id)
    setForm({ scheduled_date: f.date || '', scheduled_start: f.start_time || '', unit_label: f.court || '' })
  }

  const saveEdit = async (id) => {
    setBusy(true)
    setMessage(null)
    try {
      const res = await api.put(`/client-admin/fixtures/${id}`, form)
      setMessage({ tone: res.data.warning ? 'warn' : 'ok', text: res.data.warning || res.data.message })
      setEditing(null)
      await load()
    } catch (err) {
      setMessage({ tone: 'error', text: err.response?.data?.message || 'Could not move that match.' })
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id) => {
    if (!window.confirm('Remove this match from the draw?')) return
    setBusy(true)
    try {
      const res = await api.delete(`/client-admin/fixtures/${id}`)
      setMessage({ tone: 'ok', text: res.data.message })
      await load()
    } catch (err) {
      setMessage({ tone: 'error', text: err.response?.data?.message || 'Could not remove that match.' })
    } finally {
      setBusy(false)
    }
  }

  const addMatch = async () => {
    setBusy(true)
    setMessage(null)
    try {
      const res = await api.post(`/client-admin/events/${eventId}/fixtures`, form)
      setMessage({ tone: 'ok', text: res.data.message })
      setAdding(false)
      setForm({})
      await load()
    } catch (err) {
      setMessage({ tone: 'error', text: err.response?.data?.message || 'Could not add that match.' })
    } finally {
      setBusy(false)
    }
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })
  const categories = Object.keys(draws)

  return (
    <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
      <div style={head}>
        <div>
          <h1 style={{ margin: 0, fontSize: 25 }}>Draw &amp; Schedule</h1>
          <p style={{ margin: '4px 0 0', color: '#5c6b7f' }}>
            {total > 0 ? `${total} matches across ${categories.length} draw${categories.length === 1 ? '' : 's'}` : 'No draw built yet'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate(-1)} style={ghostBtn}>Back</button>
          <button onClick={() => { setAdding(!adding); setForm({}) }} style={ghostBtn}>
            {adding ? 'Cancel' : 'Add match'}
          </button>
          <button onClick={() => generate(total > 0)} disabled={busy} style={primaryBtn}>
            {busy ? 'Building…' : total > 0 ? 'Rebuild draw' : 'Generate draw'}
          </button>
        </div>
      </div>

      {message && (
        <div style={banner(message.tone)}>
          {message.text}
          {message.offerReplace && (
            <button onClick={() => generate(true)} style={{ ...ghostBtn, marginLeft: 12 }}>
              Rebuild anyway
            </button>
          )}
        </div>
      )}

      {adding && (
        <div style={addBox}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Add a match by hand</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10 }}>
            <label style={lbl}>Draw
              <select value={form.category || ''} onChange={set('category')} style={inp}>
                <option value="">Choose a draw…</option>
                {[...new Set(teams.map((t) => t.category))].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label style={lbl}>Home
              <select value={form.home_team_id || ''} onChange={set('home_team_id')} style={inp}>
                <option value="">To be decided</option>
                {teams.filter((t) => !form.category || t.category === form.category)
                  .map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label style={lbl}>Away
              <select value={form.away_team_id || ''} onChange={set('away_team_id')} style={inp}>
                <option value="">To be decided</option>
                {teams.filter((t) => !form.category || t.category === form.category)
                  .map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label style={lbl}>Round
              <input value={form.round_name || ''} onChange={set('round_name')} placeholder="e.g. Play-off" style={inp} />
            </label>
            <label style={lbl}>Date
              <input type="date" value={form.scheduled_date || ''} onChange={set('scheduled_date')} style={inp} />
            </label>
            <label style={lbl}>Start time
              <input type="time" value={form.scheduled_start || ''} onChange={set('scheduled_start')} style={inp} />
            </label>
            <label style={lbl}>Court
              <input value={form.unit_label || ''} onChange={set('unit_label')} placeholder="e.g. Board 4" style={inp} />
            </label>
          </div>
          <button onClick={addMatch} disabled={busy || !form.category} style={{ ...primaryBtn, marginTop: 12 }}>
            {busy ? 'Adding…' : 'Add match'}
          </button>
        </div>
      )}

      {teams.some((t) => t.kind !== 'individual') && (
        <div style={{ marginTop: 24 }}>
          <h2 style={catHeading}>
            Squads <span style={{ color: '#8b95a5', fontSize: 14, fontWeight: 500 }}>
              · {teams.filter((t) => t.kind !== 'individual').length} teams
            </span>
          </h2>
          <p style={{ color: '#5c6b7f', fontSize: 13.5, margin: '0 0 12px' }}>
            Who plays for each team. Select one to see its members.
          </p>
          {/* Teams are numbered within their own draw, so Men's Team 1 and
              Women's Team 1 both exist. Grouping by draw keeps them apart. */}
          {[...new Set(teams.filter((t) => t.kind !== 'individual').map((t) => t.category))].map((cat) => (
            <div key={cat} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#5c6b7f', marginBottom: 6 }}>{cat}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {teams.filter((t) => t.kind !== 'individual' && t.category === cat).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setOpenTeam(openTeam === t.id ? null : t.id)}
                    aria-pressed={openTeam === t.id}
                    style={{
                      padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                      border: `1px solid ${openTeam === t.id ? '#a4262c' : '#d9dee5'}`,
                      background: openTeam === t.id ? '#a4262c' : '#fff',
                      color: openTeam === t.id ? '#fff' : '#42505f',
                      fontWeight: openTeam === t.id ? 700 : 500,
                    }}
                  >
                    {t.name} <span style={{ opacity: 0.7 }}>({t.member_count})</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {openTeam && (() => {
            const team = teams.find((t) => t.id === openTeam)
            if (!team) return null
            return (
              <div style={squadBox}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>
                  {team.name}
                  <span style={{ color: '#8b95a5', fontWeight: 500, fontSize: 13 }}>
                    {' '}· {team.category}{team.city ? ` · ${team.city}` : ''}
                  </span>
                </div>
                {team.members?.length ? (
                  <ol style={{ margin: 0, paddingLeft: 20, columns: 2, columnGap: 32 }}>
                    {team.members.map((m) => (
                      <li key={m.player_id} style={{ fontSize: 13.5, padding: '3px 0', breakInside: 'avoid' }}>
                        {m.name}
                        <span style={{ color: '#8b95a5' }}> · {m.reg_id}</span>
                        {m.is_captain && <span style={captainTag}>Captain</span>}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p style={{ color: '#8b95a5', margin: 0 }}>No members recorded for this team.</p>
                )}
              </div>
            )
          })()}
        </div>
      )}

      {loading ? (
        <p style={{ marginTop: 20 }}>Loading…</p>
      ) : total === 0 ? (
        <div style={empty}>
          <strong>No matches yet.</strong>
          <p style={{ margin: '6px 0 0', color: '#5c6b7f' }}>
            Generating the draw forms teams and pairs from the approved entries,
            builds the bracket and lays it onto the event's dates and courts.
          </p>
        </div>
      ) : (
        categories.map((cat) => (
          <div key={cat} style={{ marginTop: 26 }}>
            <h2 style={catHeading}>{cat} <span style={{ color: '#8b95a5', fontSize: 14, fontWeight: 500 }}>· {draws[cat].length} matches</span></h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead>
                  <tr>
                    {['Round', '#', 'Match', 'Date', 'Time', 'Court', 'Result', ''].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {draws[cat].slice(0, 60).map((f) => (
                    <tr key={f.id}>
                      <td style={td}>{f.round}{f.group ? ` · Grp ${f.group}` : ''}</td>
                      <td style={td}>{f.match_no}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{f.home} <span style={{ color: '#a0a9b5' }}>vs</span> {f.away}</td>
                      {editing === f.id ? (
                        <>
                          <td style={td}><input type="date" value={form.scheduled_date} onChange={set('scheduled_date')} style={inpSm} /></td>
                          <td style={td}><input type="time" value={form.scheduled_start} onChange={set('scheduled_start')} style={inpSm} /></td>
                          <td style={td}><input value={form.unit_label} onChange={set('unit_label')} style={inpSm} /></td>
                          <td style={td} colSpan={2}>
                            <button onClick={() => saveEdit(f.id)} disabled={busy} style={miniPrimary}>Save</button>
                            <button onClick={() => setEditing(null)} style={miniGhost}>Cancel</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={td}>{f.date || '—'}</td>
                          <td style={td}>{f.start_time || '—'}</td>
                          <td style={td}>{f.court || '—'}</td>
                          <td style={td}>
                            {f.status === 'completed'
                              ? `${f.home_score} – ${f.away_score}`
                              : <span style={{ color: '#8b95a5' }}>{f.status}</span>}
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <button onClick={() => startEdit(f)} style={miniGhost}>Move</button>
                            <button onClick={() => remove(f.id)} style={miniDanger}>Remove</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {draws[cat].length > 60 && (
                <p style={{ color: '#8b95a5', fontSize: 13, marginTop: 8 }}>
                  Showing the first 60 of {draws[cat].length} matches.
                </p>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

const head = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }
const primaryBtn = { padding: '9px 20px', background: '#a4262c', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 700 }
const ghostBtn = { padding: '9px 18px', background: '#fff', border: '1px solid #d9dee5', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }
const empty = { padding: 26, marginTop: 20, background: '#f7f9fb', border: '1px dashed #d3dae2', borderRadius: 10 }
const catHeading = { fontSize: 18, margin: '0 0 10px', paddingBottom: 7, borderBottom: '2px solid #e4e9ef' }
const th = { textAlign: 'left', padding: '9px 12px', borderBottom: '2px solid #dde3ea', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5c6b7f' }
const td = { padding: '9px 12px', borderBottom: '1px solid #eef1f5', fontSize: 13.5 }
const squadBox = { marginTop: 14, padding: 16, background: '#fbfcfd', border: '1px solid #e4e9ef', borderRadius: 10 }
const captainTag = { marginLeft: 8, padding: '1px 7px', borderRadius: 4, background: '#eef4fb', color: '#28527a', fontSize: 11, fontWeight: 700 }
const addBox = { marginTop: 18, padding: 18, background: '#fbfcfd', border: '1px solid #e4e9ef', borderRadius: 10 }
const lbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: '#5c6b7f', fontWeight: 600 }
const inp = { padding: '8px 10px', border: '1px solid #d9dee5', borderRadius: 7, fontSize: 13.5 }
const inpSm = { padding: '5px 7px', border: '1px solid #d9dee5', borderRadius: 6, fontSize: 12.5, width: '100%' }
const miniGhost = { padding: '4px 10px', marginRight: 6, border: '1px solid #d9dee5', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 12 }
const miniPrimary = { padding: '4px 12px', marginRight: 6, border: 0, background: '#a4262c', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }
const miniDanger = { padding: '4px 10px', border: '1px solid #f0c9c6', background: '#fff', color: '#a4262c', borderRadius: 6, cursor: 'pointer', fontSize: 12 }
const banner = (tone) => ({
  marginTop: 16, padding: 13, borderRadius: 8, fontSize: 14,
  background: tone === 'ok' ? '#e9f6ee' : tone === 'warn' ? '#fdf5e3' : '#fdeceb',
  border: `1px solid ${tone === 'ok' ? '#bfe3cc' : tone === 'warn' ? '#f0dcae' : '#f3c4c0'}`,
  color: tone === 'ok' ? '#1c6b3a' : tone === 'warn' ? '#8a6412' : '#a4262c',
})

export default EventFixtures
